import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
    Card, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, InputLabel, MenuItem,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { TEXTO_COLORIDO_ESCURO, coresDoTema } from "assets/coresTema";
import { get, post, postNoResponse } from "utils/WebService";
import AnexosSolicitacao from "./AnexosSolicitacao";
import NovaSolicitacaoDialog from "./NovaSolicitacaoDialog";
import {
    ETAPAS, EVENTO_SOLICITACOES_ALTERADAS, acaoHistorico, alvoSolicitacao, atualizarNotificacoes, avisarSolicitacoesAlteradas,
    correspondeBusca, etapaDaSolicitacao, fichaDaSolicitacao, formatarDuracao, listaDe, tipoDaSolicitacao,
} from "./solicitacaoUtils";

const rotulo = { px: 1, pb: 0.5, pt: 0.2 };
const PERIODOS = [30, 90, 180, 365];
// As colunas dos cards, na ordem em que a solicitação anda (a recusa encerrada vai para Concluído)
const COLUNAS = ["SOLICITADA", "EM_ANDAMENTO", "AGUARDANDO_VOCE", "CONCLUIDA"];
const LIMITE_CONCLUIDAS = 30;
const ATUALIZAR_A_CADA_MS = 30000;

/** A cor de uma etapa: a do tema no claro; no escuro, o tom claro que se lê sobre o cinza dos blocos. */
const corDaEtapa = (theme, cor, darkMode, cores) => (darkMode
    ? (TEXTO_COLORIDO_ESCURO[cor] || cores.textoFraco)
    : (theme.palette[cor]?.main || cores.textoFraco));

/** O que o card diz da etapa: há quanto tempo espera, com quem está, o último andamento, a recusa ou a resposta. */
function SituacaoNoCard({ s, agora }) {
    const etapa = etapaDaSolicitacao(s);
    const linha = (texto, opcoes = {}) => (
        <MDTypography variant="caption" color="text" display="block" sx={{ opacity: 0.9, ...opcoes }}>{texto}</MDTypography>
    );
    const citacao = (texto) => (
        <MDTypography variant="caption" color="text" display="block"
            sx={{ mt: 0.5, fontStyle: "italic", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            “{texto}”
        </MDTypography>
    );
    // "há 5 min"; com menos de 1 minuto, "agora mesmo"
    const ha = (desde) => {
        const tempo = formatarDuracao(agora - (desde || agora));
        return tempo === "agora" ? "agora mesmo" : `há ${tempo}`;
    };
    if (etapa === "SOLICITADA") {
        return linha(s.reenviada && s.dataPosicao
            ? `Reenviada em ${s.dataPosicao}; aguardando o suporte`
            : `Enviada ${ha(s.dataSolicitacaoMillis)}; aguardando o suporte`);
    }
    if (etapa === "EM_ANDAMENTO") {
        // Migração para o Jurídico: primeiro com o CobJur, que preenche os dados e envia ao suporte
        const texto = s.status === "JURIDICO"
            ? "Com o Jurídico (CobJur), que preenche os dados e envia ao suporte"
            : s.nomeAtendente ? `Com ${s.nomeAtendente}, iniciado ${ha(s.dataInicioAtendimentoMillis)}` : "Aguardando o suporte";
        return (
            <>
                {linha(texto)}
                {s.ultimoAndamento && citacao(s.ultimoAndamento)}
            </>
        );
    }
    if (etapa === "AGUARDANDO_VOCE") {
        return (
            <>
                {linha(`Recusada${s.nomeResponsavel ? ` por ${s.nomeResponsavel}` : ""}${s.qtdCobrancas > 0 ? ` · cobrada ${s.qtdCobrancas}×` : ""}: abra e responda`, { fontWeight: "bold" })}
                {s.motivoRecusa && citacao(s.motivoRecusa)}
            </>
        );
    }
    if (etapa === "ENCERRADA") {
        return linha(`Recusa encerrada${s.dataResolucao ? ` em ${s.dataResolucao}` : ""}`);
    }
    return (
        <>
            {linha(`${s.tipoSolicitacao === "MANUAL" ? "Concluída" : "Aprovada"}${s.nomeResponsavel ? ` por ${s.nomeResponsavel}` : ""}${s.dataResolucao ? ` em ${s.dataResolucao}` : ""}`)}
            {s.resposta && citacao(s.resposta)}
        </>
    );
}

SituacaoNoCard.propTypes = { s: PropTypes.object.isRequired, agora: PropTypes.number.isRequired };

function CardSolicitacao({ s, agora, onAbrir }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const cores = coresDoTema(darkMode);
    const tipo = tipoDaSolicitacao(s);
    const etapa = etapaDaSolicitacao(s);
    const cor = ETAPAS[etapa]?.cor || "info";
    return (
        <MDBox
            role="button"
            tabIndex={0}
            onClick={() => onAbrir(s)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onAbrir(s); } }}
            p={1.5}
            mb={1.25}
            sx={(theme) => ({
                cursor: "pointer",
                borderRadius: "8px",
                background: cores.cartao,
                border: `1px solid ${s.novoRetorno ? corDaEtapa(theme, cor, darkMode, cores) : cores.borda}`,
                borderLeft: `4px solid ${corDaEtapa(theme, cor, darkMode, cores)}`,
                boxShadow: s.novoRetorno ? `0 0 0 2px ${alpha(corDaEtapa(theme, cor, darkMode, cores), 0.3)}` : "none",
                transition: "box-shadow 0.15s ease-in-out",
                "&:hover, &:focus-visible": { boxShadow: `0 2px 10px ${alpha(corDaEtapa(theme, cor, darkMode, cores), 0.35)}`, outline: "none" },
            })}
        >
            <MDBox display="flex" alignItems="center" gap={0.75} mb={0.5}>
                <Icon sx={{ color: darkMode ? tipo.corEscura : tipo.cor, fontSize: "1.05rem !important" }}>{tipo.icone}</Icon>
                <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: 1, minWidth: 0 }} noWrap>{tipo.label}</MDTypography>
                {s.novoRetorno && (
                    <Chip size="small" color={cor === "secondary" ? "default" : cor} icon={<Icon>fiber_new</Icon>} label="Novo retorno"
                        sx={{ height: 20, fontSize: 10, "& .MuiChip-icon": { fontSize: "0.95rem" } }} />
                )}
            </MDBox>
            <MDTypography variant="caption" fontWeight="bold" color="text" display="block" noWrap>{s.nomeDevedor || "-"}</MDTypography>
            <MDTypography variant="caption" color="text" display="block" sx={{ opacity: 0.85 }} noWrap>
                {[fichaDaSolicitacao(s), s.nomeCliente].filter(Boolean).join(" · ") || s.cpfCnpjDevedor || "-"}
            </MDTypography>
            {s.observacao && (
                <MDTypography variant="caption" color="text" display="block" mt={0.5}
                    sx={{ opacity: 0.85, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {s.observacao}
                </MDTypography>
            )}
            <MDBox mt={0.75}><SituacaoNoCard s={s} agora={agora} /></MDBox>
            <MDTypography variant="caption" color="text" display="block" mt={0.75} sx={{ opacity: 0.7, fontSize: "0.68rem" }}>
                Pedida em {s.dataSolicitacao || "-"}
            </MDTypography>
        </MDBox>
    );
}

CardSolicitacao.propTypes = { s: PropTypes.object.isRequired, agora: PropTypes.number.isRequired, onAbrir: PropTypes.func.isRequired };

/** Mais recente (ou com retorno novo) primeiro. */
const ordenar = (lista) => [...lista].sort((a, b) => (Number(b.novoRetorno) - Number(a.novoRetorno))
    || ((b.ultimoRetornoMillis || b.dataSolicitacaoMillis || 0) - (a.ultimoRetornoMillis || a.dataSolicitacaoMillis || 0)));

/**
 * Solicitações ao Suporte (c089; pedido da Aline em 23/10/2026): um lugar só para pedir ao suporte e acompanhar os
 * pedidos, sem botão em cada tela. As solicitações do próprio usuário em cards por etapa (Solicitado, Em andamento,
 * Aguardando você, Concluído), que andam conforme o suporte atende: iniciar o atendimento leva para Em andamento; o
 * andamento, a conclusão e a recusa aparecem no card como "Novo retorno" (e o sino pisca) até ele abrir. A tela relê
 * a cada 30 s enquanto está à vista. "Nova solicitação" abre a janela com os tipos da lista do suporte.
 */
export default function MinhasSolicitacoes() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const cores = coresDoTema(darkMode);
    const raiz = useRef(null);
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [busca, setBusca] = useState("");
    const [dias, setDias] = useState(90);
    const [agora, setAgora] = useState(Date.now());
    const [limiteConcluidas, setLimiteConcluidas] = useState(LIMITE_CONCLUIDAS);
    const [novaAberta, setNovaAberta] = useState(false);
    const [selecionada, setSelecionada] = useState(null);
    const [historico, setHistorico] = useState([]);
    const [carregandoHistorico, setCarregandoHistorico] = useState(false);
    const [resposta, setResposta] = useState(null); // { modo: "ciente" | "reenviar", texto }
    const [salvando, setSalvando] = useState(false);
    const [aviso, setAviso] = useState({ aberto: false, ok: true, texto: "" });

    const avisar = (texto, ok = true) => setAviso({ aberto: true, ok, texto });

    const carregar = useCallback((silencioso = false) => {
        if (!silencioso) setCarregando(true);
        return get(`/solicitacoes-suporte/minhas?dias=${dias}`)
            .then(res => { setDados(res || {}); setAgora(Date.now()); })
            .catch(e => { if (!silencioso) avisar(e.message || "Erro ao carregar as suas solicitações.", false); })
            .finally(() => { if (!silencioso) setCarregando(false); });
    }, [dias]);

    useEffect(() => { carregar(); }, [carregar]);

    // Outra tela (a ficha, o sino) criou ou mudou uma solicitação, ou o sino viu um retorno novo: recarrega
    useEffect(() => {
        const recarregar = () => carregar(true);
        window.addEventListener(EVENTO_SOLICITACOES_ALTERADAS, recarregar);
        return () => window.removeEventListener(EVENTO_SOLICITACOES_ALTERADAS, recarregar);
    }, [carregar]);

    // Os cards andam sozinhos: relê a cada 30 s e ao voltar para a janela, só com a tela à vista (a aba escondida do
    // SystemWrapper não tem offsetParent)
    useEffect(() => {
        const aVista = () => document.visibilityState === "visible" && !!raiz.current?.offsetParent;
        const timer = setInterval(() => { if (aVista()) carregar(true); }, ATUALIZAR_A_CADA_MS);
        const aoVoltar = () => { if (aVista()) carregar(true); };
        window.addEventListener("focus", aoVoltar);
        return () => { clearInterval(timer); window.removeEventListener("focus", aoVoltar); };
    }, [carregar]);

    const abertas = useMemo(() => (dados?.abertas || []).filter(s => correspondeBusca(s, busca)), [dados, busca]);
    const concluidas = useMemo(() => (dados?.concluidas || []).filter(s => correspondeBusca(s, busca)), [dados, busca]);
    const porColuna = useMemo(() => ({
        SOLICITADA: ordenar(abertas.filter(s => etapaDaSolicitacao(s) === "SOLICITADA")),
        EM_ANDAMENTO: ordenar(abertas.filter(s => etapaDaSolicitacao(s) === "EM_ANDAMENTO")),
        AGUARDANDO_VOCE: ordenar(abertas.filter(s => etapaDaSolicitacao(s) === "AGUARDANDO_VOCE")),
        CONCLUIDA: ordenar(concluidas),
    }), [abertas, concluidas]);
    const novosRetornos = useMemo(() => [...(dados?.abertas || []), ...(dados?.concluidas || [])].filter(s => s.novoRetorno).length, [dados]);

    const marcarVistoNaTela = (ids) => setDados(d => (d ? {
        ...d,
        abertas: (d.abertas || []).map(x => (ids.includes(x.id) ? { ...x, novoRetorno: false } : x)),
        concluidas: (d.concluidas || []).map(x => (ids.includes(x.id) ? { ...x, novoRetorno: false } : x)),
    } : d));

    const abrirDetalhes = (s) => {
        setSelecionada(s);
        setResposta(null);
        setHistorico([]);
        setCarregandoHistorico(true);
        get(`/solicitacoes-suporte/${s.id}/historico`)
            .then(res => setHistorico(listaDe(res)))
            .catch(() => setHistorico([]))
            .finally(() => setCarregandoHistorico(false));
        // Abriu o retorno do suporte: deixa de ser novidade (o card e o sino param de chamar a atenção)
        if (s.novoRetorno) {
            postNoResponse(`/solicitacoes-suporte/${s.id}/visto`, {})
                .then(() => { marcarVistoNaTela([s.id]); atualizarNotificacoes(); })
                .catch(() => null);
        }
    };

    const marcarTodosVistos = () => {
        post("/solicitacoes-suporte/retornos-vistos", {})
            .then(() => {
                marcarVistoNaTela([...(dados?.abertas || []), ...(dados?.concluidas || [])].map(x => x.id));
                atualizarNotificacoes();
            })
            .catch(e => avisar(e.message || "Erro ao marcar os retornos como vistos.", false));
    };

    const responder = async () => {
        const reenviar = resposta?.modo === "reenviar";
        if (reenviar && !(resposta.texto || "").trim()) {
            avisar("Informe a nova justificativa para reenviar.", false);
            return;
        }
        setSalvando(true);
        try {
            if (reenviar) {
                await postNoResponse(`/solicitacoes-suporte/${selecionada.id}/reenviar`, { justificativa: resposta.texto.trim() });
            } else {
                await postNoResponse(`/solicitacoes-suporte/${selecionada.id}/ciente`, { comentario: (resposta.texto || "").trim() || null });
            }
            avisar(reenviar ? "Solicitação reenviada para a fila do suporte." : "Ciência registrada: a solicitação foi encerrada.");
            avisarSolicitacoesAlteradas();
            atualizarNotificacoes();
            setSelecionada(null);
            carregar();
        } catch (e) {
            avisar(e.message || "Erro ao responder.", false);
        } finally {
            setSalvando(false);
        }
    };

    const coluna = (etapa) => {
        const def = ETAPAS[etapa];
        const lista = porColuna[etapa];
        const mostradas = etapa === "CONCLUIDA" ? lista.slice(0, limiteConcluidas) : lista;
        return (
            <Grid item xs={12} sm={6} lg={3} key={etapa}>
                <Card sx={{ p: 1.5, height: "100%", background: cores.painelEscuro }}>
                    <MDBox display="flex" alignItems="center" gap={1} px={0.5} pb={0.5}>
                        <Icon sx={(theme) => ({ color: corDaEtapa(theme, def.cor, darkMode, cores) })}>{def.icone}</Icon>
                        <MDTypography variant="button" fontWeight="bold" color="text" sx={{ flex: 1 }}>{def.label}</MDTypography>
                        <MDBox px={1} sx={(theme) => ({
                            borderRadius: "10px", minWidth: 24, textAlign: "center",
                            background: alpha(corDaEtapa(theme, def.cor, darkMode, cores), darkMode ? 0.3 : 0.15),
                        })}>
                            <MDTypography variant="caption" fontWeight="bold" color="text">{lista.length}</MDTypography>
                        </MDBox>
                    </MDBox>
                    <MDTypography variant="caption" color="text" display="block" px={0.5} mb={1} sx={{ opacity: 0.8 }}>
                        {etapa === "CONCLUIDA" ? `Nos últimos ${dados?.dias || dias} dias.` : def.ajuda}
                    </MDTypography>
                    <MDBox sx={{ maxHeight: { lg: "calc(100vh - 330px)" }, overflowY: { lg: "auto" }, pr: { lg: 0.5 } }}>
                        {mostradas.length === 0 ? (
                            <MDBox py={3} textAlign="center" sx={{ border: `1px dashed ${cores.borda}`, borderRadius: "8px" }}>
                                <MDTypography variant="caption" color="text" sx={{ opacity: 0.75 }}>Nenhuma solicitação.</MDTypography>
                            </MDBox>
                        ) : mostradas.map(s => <CardSolicitacao key={s.id} s={s} agora={agora} onAbrir={abrirDetalhes} />)}
                        {lista.length > mostradas.length && (
                            <MDBox textAlign="center">
                                <MDButton variant="text" color="info" size="small" onClick={() => setLimiteConcluidas(l => l + LIMITE_CONCLUIDAS)}>
                                    Mostrar mais ({lista.length - mostradas.length})
                                </MDButton>
                            </MDBox>
                        )}
                    </MDBox>
                </Card>
            </Grid>
        );
    };

    return (
        <MDBox ref={raiz}>
            <Card sx={{ px: 2, py: 1.5, mb: 2 }}>
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={6} md={4}>
                        <InputLabel sx={rotulo}>Buscar</InputLabel>
                        <MDInput fullWidth size="small" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Devedor, CPF/CNPJ, tipo, ficha" />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <InputLabel sx={rotulo}>Concluídas nos últimos</InputLabel>
                        <MDInput select fullWidth size="small" value={dias} onChange={e => { setDias(e.target.value); setLimiteConcluidas(LIMITE_CONCLUIDAS); }}>
                            {PERIODOS.map(p => <MenuItem key={p} value={p}>{p} dias</MenuItem>)}
                        </MDInput>
                    </Grid>
                    <Grid item xs display="flex" justifyContent="flex-end" gap={1} flexWrap="wrap">
                        <MDButton variant="outlined" color="info" size="medium" onClick={() => carregar()} disabled={carregando} sx={{ height: 38 }}>
                            <Icon sx={{ mr: 0.5 }}>refresh</Icon>Atualizar
                        </MDButton>
                        {dados?.podePedir && (
                            <MDButton variant="contained" color="info" size="medium" onClick={() => setNovaAberta(true)} sx={{ height: 38 }}>
                                <Icon sx={{ mr: 0.5 }}>add</Icon>Nova solicitação
                            </MDButton>
                        )}
                    </Grid>
                    <Grid item xs={12} sx={{ pt: "8px !important" }}>
                        {novosRetornos > 0 ? (
                            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" px={1}>
                                <MDTypography variant="caption" fontWeight="bold" sx={{ color: darkMode ? `${TEXTO_COLORIDO_ESCURO.warning} !important` : undefined }}
                                    color={darkMode ? undefined : "warning"}>
                                    {novosRetornos === 1 ? "1 retorno do suporte que você ainda não viu" : `${novosRetornos} retornos do suporte que você ainda não viu`}
                                    {" "}(marcados com &quot;Novo retorno&quot;): abra o card para ver.
                                </MDTypography>
                                {novosRetornos > 1 && (
                                    <MDButton variant="text" color="info" size="small" onClick={marcarTodosVistos} sx={{ py: 0 }}>Marcar todos como vistos</MDButton>
                                )}
                            </MDBox>
                        ) : (
                            <MDTypography variant="caption" color="text" display="block" px={1} sx={{ opacity: 0.85 }}>
                                Os cards andam conforme o suporte atende: quando alguém do suporte inicia o atendimento, o pedido vai para
                                Em andamento; o andamento, a conclusão e a recusa aparecem como &quot;Novo retorno&quot; e o sino pisca até você abrir.
                            </MDTypography>
                        )}
                    </Grid>
                </Grid>
            </Card>

            {carregando && !dados ? (
                <Card sx={{ p: 2 }}>
                    <MDBox display="flex" justifyContent="center" py={4}><CircularProgress size={28} color="info" /></MDBox>
                </Card>
            ) : (
                <Grid container spacing={2}>
                    {COLUNAS.map(coluna)}
                </Grid>
            )}

            <NovaSolicitacaoDialog
                open={novaAberta}
                onClose={() => setNovaAberta(false)}
                onCriada={() => { avisar("Solicitação enviada ao suporte."); carregar(); }}
            />

            {/* Detalhes */}
            <Dialog open={!!selecionada} onClose={() => setSelecionada(null)} maxWidth="md" fullWidth>
                {selecionada && (() => {
                    const tipo = tipoDaSolicitacao(selecionada);
                    const etapa = ETAPAS[etapaDaSolicitacao(selecionada)];
                    return (
                        <>
                            <DialogTitle>
                                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    <Icon sx={{ color: darkMode ? tipo.corEscura : tipo.cor }}>{tipo.icone}</Icon>
                                    {tipo.label}
                                    <Chip size="small" variant="outlined" color={etapa.cor === "secondary" ? "default" : etapa.cor}
                                        icon={<Icon>{etapa.icone}</Icon>} label={etapa.label} sx={{ ml: 1 }} />
                                </MDBox>
                            </DialogTitle>
                            <DialogContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={7}>
                                        <MDTypography variant="caption" fontWeight="bold" color="text" display="block">Devedor</MDTypography>
                                        <MDTypography variant="caption" color="text" display="block" mb={1}>
                                            {selecionada.nomeDevedor} · {[selecionada.cpfCnpjDevedor, selecionada.nomeCliente].filter(Boolean).join(" · ")}
                                        </MDTypography>
                                        <MDTypography variant="caption" fontWeight="bold" color="text" display="block">O que pediu</MDTypography>
                                        <MDTypography variant="caption" color="text" display="block" mb={1}>{alvoSolicitacao(selecionada)}</MDTypography>
                                        {selecionada.nomeAtendente && (
                                            <>
                                                <MDTypography variant="caption" fontWeight="bold" color="text" display="block">Atendimento</MDTypography>
                                                <MDTypography variant="caption" color="text" display="block" mb={1}>
                                                    {selecionada.nomeAtendente}{selecionada.dataInicioAtendimento ? `, desde ${selecionada.dataInicioAtendimento}` : ""}
                                                </MDTypography>
                                            </>
                                        )}
                                        <MDTypography variant="caption" fontWeight="bold" color="text" display="block">Descrição</MDTypography>
                                        <MDBox p={1.5} mb={1} sx={{ borderRadius: "6px", backgroundColor: cores.painel }}>
                                            <MDTypography variant="button" color="text" sx={{ whiteSpace: "pre-wrap" }}>{selecionada.observacao || "-"}</MDTypography>
                                        </MDBox>
                                        {(selecionada.status === "PENDENTE" || selecionada.status === "JURIDICO") && selecionada.ultimoAndamento && (
                                            <>
                                                <MDTypography variant="caption" fontWeight="bold" color="text" display="block">
                                                    Último andamento{selecionada.dataUltimoAndamento ? ` (${selecionada.dataUltimoAndamento})` : ""}
                                                </MDTypography>
                                                <MDBox p={1.5} mb={1} sx={{ borderRadius: "6px", backgroundColor: cores.painel, borderLeft: `3px solid ${cores.bordaForte}` }}>
                                                    <MDTypography variant="button" color="text" sx={{ whiteSpace: "pre-wrap" }}>{selecionada.ultimoAndamento}</MDTypography>
                                                </MDBox>
                                            </>
                                        )}
                                        {selecionada.resposta && (
                                            <>
                                                <MDTypography variant="caption" fontWeight="bold" color="text" display="block">Resposta do suporte</MDTypography>
                                                <MDBox p={1.5} mb={1} sx={{ borderRadius: "6px", backgroundColor: cores.painel, borderLeft: `3px solid ${cores.bordaForte}` }}>
                                                    <MDTypography variant="button" color="text" sx={{ whiteSpace: "pre-wrap" }}>{selecionada.resposta}</MDTypography>
                                                </MDBox>
                                            </>
                                        )}
                                        {selecionada.motivoRecusa && (
                                            <>
                                                <MDTypography variant="caption" fontWeight="bold" color="error" display="block">
                                                    {selecionada.status === "PENDENTE" || selecionada.status === "JURIDICO" ? "Motivo da recusa anterior" : "Motivo da recusa"}
                                                </MDTypography>
                                                <MDBox p={1.5} mb={1} sx={{ borderRadius: "6px", backgroundColor: cores.painel }}>
                                                    <MDTypography variant="button" color="text" sx={{ whiteSpace: "pre-wrap" }}>{selecionada.motivoRecusa}</MDTypography>
                                                </MDBox>
                                            </>
                                        )}
                                        <MDTypography variant="caption" fontWeight="bold" color="text" display="block">Anexos</MDTypography>
                                        <AnexosSolicitacao
                                            solicitacao={selecionada}
                                            onChange={(atualizada) => { if (atualizada) setSelecionada(atualizada); carregar(true); }}
                                            onErro={(m) => avisar(m, false)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>Linha do tempo</MDTypography>
                                        {carregandoHistorico ? <CircularProgress size={20} color="info" /> : historico.map((h, i) => {
                                            const acao = acaoHistorico(h.acao);
                                            return (
                                                <MDBox key={i} display="flex" gap={1} mb={1}>
                                                    <Icon color={acao.cor} sx={{ fontSize: "1.1rem !important", mt: 0.25 }}>{acao.icone}</Icon>
                                                    <MDBox>
                                                        <MDTypography variant="caption" fontWeight="bold" color="text" display="block">{acao.label}</MDTypography>
                                                        <MDTypography variant="caption" color="text" display="block" sx={{ opacity: 0.85 }}>
                                                            {[h.data, h.usuario].filter(Boolean).join(" · ")}
                                                        </MDTypography>
                                                        {h.descricao && (
                                                            <MDTypography variant="caption" color="text" display="block" sx={{ whiteSpace: "pre-wrap" }}>{h.descricao}</MDTypography>
                                                        )}
                                                    </MDBox>
                                                </MDBox>
                                            );
                                        })}
                                    </Grid>
                                    {selecionada.status === "REJEITADA" && (
                                        <Grid item xs={12}>
                                            {!resposta ? (
                                                <MDBox display="flex" gap={1} flexWrap="wrap">
                                                    <MDButton variant="outlined" color="secondary" size="small" onClick={() => setResposta({ modo: "ciente", texto: "" })}>
                                                        <Icon sx={{ mr: 0.5 }}>done_all</Icon>Estou ciente (encerrar)
                                                    </MDButton>
                                                    <MDButton variant="contained" color="info" size="small" onClick={() => setResposta({ modo: "reenviar", texto: "" })}>
                                                        <Icon sx={{ mr: 0.5 }}>replay</Icon>Reenviar com nova justificativa
                                                    </MDButton>
                                                </MDBox>
                                            ) : (
                                                <>
                                                    <InputLabel sx={rotulo}>
                                                        {resposta.modo === "reenviar" ? "Nova justificativa *" : "Comentário (opcional)"}
                                                    </InputLabel>
                                                    <MDInput fullWidth multiline rows={3} value={resposta.texto}
                                                        onChange={e => setResposta({ ...resposta, texto: e.target.value })} />
                                                </>
                                            )}
                                        </Grid>
                                    )}
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                {resposta && (
                                    <MDButton variant="gradient" color={resposta.modo === "reenviar" ? "info" : "secondary"} onClick={responder} disabled={salvando}>
                                        {salvando ? <CircularProgress size={18} color="inherit" /> : resposta.modo === "reenviar" ? "Reenviar ao suporte" : "Confirmar ciência"}
                                    </MDButton>
                                )}
                                <MDButton variant="text" color="secondary" onClick={() => setSelecionada(null)}>Fechar</MDButton>
                            </DialogActions>
                        </>
                    );
                })()}
            </Dialog>

            <MDSnackbar
                color={aviso.ok ? "success" : "error"}
                icon={aviso.ok ? "check" : "warning"}
                title={aviso.ok ? "Sucesso" : "Atenção"}
                content={aviso.texto}
                open={aviso.aberto}
                onClose={() => setAviso(a => ({ ...a, aberto: false }))}
                close={() => setAviso(a => ({ ...a, aberto: false }))}
                bgWhite
            />
        </MDBox>
    );
}
